import React, { createContext, useCallback, useMemo, useState } from "react";
import { authApi } from "../api/authApi";
import { AuthResponse, LoginRequest, OperatorRegisterRequest } from "../types";
import { parseJwtPayload } from "../utils/jwtUtils";

interface AuthContextValue {
  user: AuthResponse | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (payload: LoginRequest) => Promise<void>;
  register: (payload: OperatorRegisterRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_USER_KEY = "authUser";
const TOKEN_KEY = "token";

function parseUserIdFromToken(token: string): string | undefined {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return undefined;
    }
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { sub?: string };
    return typeof json.sub === "string" ? json.sub : undefined;
  } catch {
    return undefined;
  }
}

function parseTenantTypeFromToken(token: string): string {
  const payload = parseJwtPayload(token);
  const t = payload?.["tenantType"];
  return t === "diy" ? "diy" : "operator";
}

function enrichAuthResponse(response: AuthResponse): AuthResponse {
  const userId = parseUserIdFromToken(response.token);
  const tenantType = response.tenantType ?? parseTenantTypeFromToken(response.token);
  return {
    ...response,
    tenantType,
    ...(userId ? { userId } : {}),
  };
}

function getInitialUser(): AuthResponse | null {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as AuthResponse;
    return enrichAuthResponse(parsed);
  } catch {
    localStorage.removeItem(AUTH_USER_KEY);
    return null;
  }
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<AuthResponse | null>(getInitialUser);
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));

  const login = useCallback(async (payload: LoginRequest) => {
    const response = enrichAuthResponse(await authApi.login(payload));
    localStorage.setItem(TOKEN_KEY, response.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response));
    setToken(response.token);
    setUser(response);
  }, []);

  const register = useCallback(async (payload: OperatorRegisterRequest) => {
    const response = enrichAuthResponse(await authApi.register(payload));
    localStorage.setItem(TOKEN_KEY, response.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response));
    setToken(response.token);
    setUser(response);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      login,
      register,
      logout,
    }),
    [user, token, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
